(() => {
  'use strict';
  if (customElements.get('rnf-mobile-menu')) return;

  const saveStyles = (node, names) => names.map(name => [name, node.style.getPropertyValue(name), node.style.getPropertyPriority(name)]);
  const restoreStyles = (node, entries) => entries.forEach(([name, value, priority]) => value ? node.style.setProperty(name, value, priority) : node.style.removeProperty(name));
  const listenMedia = (query, listener, signal) => {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', listener);
      signal.addEventListener('abort', () => query.removeEventListener('change', listener), { once: true });
    } else if (typeof query.addListener === 'function') {
      query.addListener(listener);
      signal.addEventListener('abort', () => query.removeListener(listener), { once: true });
    }
  };
  const productURL = value => {
    try {
      const url = new URL(value, location.href);
      return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url.pathname + url.search : null;
    } catch (_) { return null; }
  };

  class RnfMobileMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.toggle = this.querySelector('[data-rnf-mm-toggle]');
      this.dialog = this.querySelector('[data-rnf-mm-dialog]');
      this.sheet = this.querySelector('[data-rnf-mm-sheet]');
      if (!this.toggle || !this.dialog || !this.sheet) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.panels = new Map([...this.querySelectorAll('[data-rnf-mm-panel]')].map(panel => [panel.dataset.rnfMmPanel, panel]));
      this.backButton = this.querySelector('[data-rnf-mm-back]');
      this.backLabel = this.querySelector('[data-rnf-mm-back-label]');
      this.handle = this.querySelector('[data-rnf-mm-close]');
      this.labels = new Map([...this.panels].map(([key, panel]) => [key, panel.dataset.rnfMmTitle || '']));
      this.path = ['root'];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 989px)');
      listenMedia(this.motion, () => { if (this.reducedMotion()) this.sheetAnimation?.finish(); }, signal);
      this.warmed = new Set();
      this.buying = false;
      this.suppressClickUntil = 0;
      this.keyboardFocus = false;
      this.dataset.rnfMmState = 'closed';
      this.toggle.setAttribute('aria-haspopup', 'dialog');
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.dialog.id) this.toggle.setAttribute('aria-controls', this.dialog.id);
      if (!this.dialog.hasAttribute('tabindex')) this.dialog.tabIndex = -1;
      // Native modal focus must not scroll the off-screen close handle into view.
      this.dialog.setAttribute('autofocus', '');
      this.resetPanels();
      this.installHeaderMotion();
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.dialog.addEventListener('click', event => { if (!this.contains(this.dialog)) this.onClick(event); }, { signal });
      this.dialog.addEventListener('pointerover', event => this.warmIntent(event), { signal, passive: true });
      this.dialog.addEventListener('focusin', event => this.warmIntent(event), { signal });
      this.dialog.addEventListener('touchstart', event => this.warmIntent(event), { signal, passive: true });
      document.addEventListener('keydown', event => this.onFallbackKeydown(event), { signal });
      document.addEventListener('click', event => {
        const button = event.target.closest?.('[data-rnf-store-products] [data-rnf-mm-buy]');
        if (!button || this.owns(button)) return;
        event.preventDefault();
        this.buy(button, event, true);
      }, { signal });
      this.addEventListener('pointerover', event => this.warmIntent(event), { signal, passive: true });
      this.addEventListener('focusin', event => this.warmIntent(event), { signal });
      this.addEventListener('touchstart', event => this.warmIntent(event), { signal, passive: true });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.keyboardFocus = true;
        if (this.path.length > 1) this.back();
        else this.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.finishing && !this.isOpen() && (this.scrollLock || this.closePromise)) this.finishClose(false);
      }, { signal });
      window.addEventListener('resize', () => {
        if (!this.mobile.matches && this.isOpen()) this.close({ immediate: true });
        else this.viewport();
      }, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.viewport(), { signal, passive: true });
      window.visualViewport?.addEventListener('scroll', () => this.viewport(), { signal, passive: true });
      window.addEventListener('focus', () => {
        this.warmed.clear();
        if (this.isOpen()) this.warmPanel(this.panels.get(this.dataset.panel));
      }, { signal });
      this.bindDrag(signal);
      const reviewAnchor = () => {
        const section = this.findReviews();
        if (section && location.hash === '#rnf-customer-reviews') {
          this.reviewFrame = requestAnimationFrame(() => { this.reviewFrame = null; if (this.isConnected) section.scrollIntoView({ behavior: 'auto', block: 'start' }); });
        }
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reviewAnchor, { signal, once: true });
      else reviewAnchor();
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      cancelAnimationFrame(this.reviewFrame);
      this.reviewFrame = null;
      this.finishClose(false);
      this.restoreDialog();
      this.proxy?.remove();
      this.proxy = null;
      this.buying = false;
    }

    reducedMotion() { return this.motion?.matches; }

    isOpen() { return !!this.dialog?.hasAttribute('open'); }

    owns(node) { return this.contains(node) || this.dialog?.contains(node); }

    portalDialog() {
      if (this.dialog.parentElement === document.body) return;
      this.dialogHome = { parent: this.dialog.parentNode, next: this.dialog.nextSibling };
      this.dialog.setAttribute('dir', this.getAttribute('dir') || document.documentElement.dir || 'ltr');
      document.body.append(this.dialog);
    }

    restoreDialog() {
      const home = this.dialogHome;
      if (!home) return;
      this.dialogHome = null;
      if (home.next?.parentNode === home.parent) home.parent.insertBefore(this.dialog, home.next);
      else home.parent.appendChild(this.dialog);
    }

    openSurface() {
      this.portalDialog();
      this.nativeModal = false;
      if (typeof this.dialog.showModal === 'function' && typeof this.dialog.close === 'function') {
        const sheetInert = this.sheet.hasAttribute('inert');
        this.sheet.setAttribute('inert', '');
        try {
          this.dialog.showModal();
          this.nativeModal = this.isOpen();
          if (this.nativeModal) return;
        } catch (_) {
          // Embedded browsers can expose a dialog API that cannot open a modal.
          if (this.isOpen() && typeof this.dialog.close === 'function') this.dialog.close();
        } finally { if (!sheetInert) this.sheet.removeAttribute('inert'); }
      }
      this.dialog.classList.add('is-fallback');
      this.dialog.setAttribute('open', '');
      this.dialog.setAttribute('role', 'dialog');
      this.dialog.setAttribute('aria-modal', 'true');
      this.background = [...document.querySelectorAll('#MainContent,.shopify-section-group-header-group,.shopify-section-group-footer-group')].map(node => ({
        node, inert: node.hasAttribute('inert'), hidden: node.getAttribute('aria-hidden')
      }));
      this.background.forEach(({ node }) => {
        node.setAttribute('inert', '');
        node.setAttribute('aria-hidden', 'true');
      });
    }

    closeSurface() {
      const wasOpen = this.isOpen();
      if (this.nativeModal && wasOpen) this.dialog.close();
      else if (wasOpen) {
        this.dialog.removeAttribute('open');
        window.setTimeout(() => this.dialog.dispatchEvent(new Event('close')), 0);
      }
      this.nativeModal = false;
      this.dialog.classList.remove('is-fallback');
      this.background?.forEach(({ node, inert, hidden }) => {
        if (!inert) node.removeAttribute('inert');
        if (hidden === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', hidden);
      });
      this.background = null;
      this.restoreDialog();
    }

    onFallbackKeydown(event) {
      if (!this.isOpen() || !this.dialog.classList.contains('is-fallback')) return;
      if ([...document.querySelectorAll('dialog[open]')].some(dialog => dialog !== this.dialog)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.keyboardFocus = true;
        if (this.path.length > 1) this.back();
        else this.close();
      } else if (event.key === 'Tab') {
        const items = [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')].filter(node =>
          !node.disabled && node.tabIndex >= 0 && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden'
        );
        const first = items[0], last = items[items.length - 1];
        if (!first) { event.preventDefault(); this.dialog.focus({ preventScroll: true }); }
        else if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
          event.preventDefault(); last.focus({ preventScroll: true });
        } else if (!event.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement) || document.activeElement === this.dialog)) {
          event.preventDefault(); first.focus({ preventScroll: true });
        }
      }
    }

    findReviews() {
      const section = document.querySelector('section.rnf-reviews-slider');
      if (!section) return null;
      if (!document.getElementById('rnf-customer-reviews')) {
        const anchor = document.createElement('span');
        anchor.id = 'rnf-customer-reviews';
        anchor.setAttribute('aria-hidden', 'true');
        anchor.style.display = 'block';
        anchor.style.height = '0';
        section.prepend(anchor);
      }
      return section;
    }

    async visitReviews(link, event) {
      const url = new URL(link.href, location.href);
      const section = this.findReviews();
      if (!section || url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== location.pathname.replace(/\/$/, '')) return false;
      event.preventDefault();
      await this.close({ restoreFocus: false });
      if (!this.isConnected) return true;
      const target = new URL(location.href);
      target.hash = 'rnf-customer-reviews';
      history.replaceState(history.state, '', target.href);
      section.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      if (!section.hasAttribute('tabindex')) section.tabIndex = -1;
      section.focus({ preventScroll: true });
      return true;
    }

    viewport(force = false) {
      if (!this.isOpen()) return;
      if (force) {
        cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = null;
        this.updateViewport();
        return;
      }
      if (this.sheetAnimation || this.viewportFrame) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        if (this.isOpen() && !this.sheetAnimation) this.updateViewport();
      });
    }

    updateViewport() {
      const view = window.visualViewport;
      const height = Math.round(view?.height || innerHeight);
      const bottom = Math.max(0, Math.round(innerHeight - (view?.height || innerHeight) - (view?.offsetTop || 0)));
      if (height !== this.viewportHeight) {
        this.viewportHeight = height;
        this.dialog.style.setProperty('--rnf-mm-height', `${height}px`);
      }
      if (bottom !== this.viewportBottom) {
        this.viewportBottom = bottom;
        this.dialog.style.setProperty('--rnf-mm-bottom', `${bottom}px`);
      }
    }

    cancelSheetAnimation() {
      cancelAnimationFrame(this.sheetFrame);
      this.sheetFrame = null;
      const animation = this.sheetAnimation;
      this.sheetAnimation = null;
      if (animation) { animation.onfinish = null; animation.oncancel = null; animation.cancel(); }
      this.sheet?.classList.remove('is-animating');
    }

    animateSheet(from, to, finish) {
      if (this.reducedMotion() || typeof this.sheet.animate !== 'function') { finish(); return; }
      this.sheet.classList.add('is-animating');
      let animation;
      try {
        animation = this.sheet.animate([{ transform: from }, { transform: to }], {
          duration: 600, easing: 'cubic-bezier(.7,0,.2,1)', fill: 'both'
        });
        animation.pause();
        animation.currentTime = 0;
      } catch (_) {
        animation?.cancel();
        this.sheet.classList.remove('is-animating');
        finish();
        return;
      }
      this.sheetAnimation = animation;
      const complete = () => {
        if (this.sheetAnimation !== animation) return;
        this.cancelSheetAnimation();
        finish();
      };
      animation.onfinish = complete;
      animation.oncancel = complete;
      let elapsed = 0, previous;
      const advance = timestamp => {
        if (this.sheetAnimation !== animation) return;
        this.sheetFrame = null;
        // Busy home sections must not consume the animation between rendered frames.
        if (previous !== undefined) elapsed += Math.min(34, Math.max(0, timestamp - previous));
        previous = timestamp;
        try { animation.currentTime = Math.min(600, elapsed); }
        catch (_) { complete(); return; }
        if (elapsed >= 600) complete();
        else this.sheetFrame = requestAnimationFrame(advance);
      };
      this.sheetFrame = requestAnimationFrame(advance);
    }

    installHeaderMotion() {
      const header = this.closest('.header');
      if (!header) return;
      const controls = [...header.querySelectorAll('.header__icons .header__icon,[data-rnf-mm-toggle]')];
      controls.forEach(control => control.classList.add('rnf-mobile-header-icon'));
      const signal = this.controller.signal;
      let active = null, frame = 0, x = 0, y = 0;
      const reset = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        if (!active) return;
        active.classList.remove('is-mobile-magnetized');
        active.style.removeProperty('--rnf-mobile-mx');
        active.style.removeProperty('--rnf-mobile-my');
        active = null;
      };
      const move = event => {
        if (!this.mobile.matches || this.reducedMotion()) return;
        if (event.type !== 'pointerdown' && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        const control = event.target.closest?.('.rnf-mobile-header-icon');
        if (!control || !header.contains(control)) return;
        if (active !== control) reset();
        active = control;
        const rect = control.getBoundingClientRect();
        const strength = event.pointerType === 'touch' ? .1 : .16;
        x = Math.max(-4, Math.min(4, (event.clientX - rect.left - rect.width / 2) * strength));
        y = Math.max(-3, Math.min(3, (event.clientY - rect.top - rect.height / 2) * strength));
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (!active) return;
          active.classList.add('is-mobile-magnetized');
          active.style.setProperty('--rnf-mobile-mx', x.toFixed(2) + 'px');
          active.style.setProperty('--rnf-mobile-my', y.toFixed(2) + 'px');
        });
      };
      header.addEventListener('pointermove', move, { signal, passive: true });
      header.addEventListener('pointerdown', move, { signal, passive: true });
      header.addEventListener('pointerout', event => {
        if (active && !active.contains(event.relatedTarget)) reset();
      }, { signal, passive: true });
      header.addEventListener('pointerup', event => {
        if (event.pointerType !== 'mouse') reset();
      }, { signal, passive: true });
      header.addEventListener('pointercancel', reset, { signal, passive: true });
      header.addEventListener('focusout', reset, { signal });
      listenMedia(this.mobile, reset, signal);
      listenMedia(this.motion, reset, signal);
      signal.addEventListener('abort', () => {
        reset();
        controls.forEach(control => control.classList.remove('rnf-mobile-header-icon'));
      }, { once: true });
    }


    canScrollMenu(target, delta) {
      if (!delta || !this.dialog.contains(target)) return false;
      for (let node = target; node && node !== this.dialog; node = node.parentElement) {
        if (!/^(auto|scroll|overlay)$/.test(getComputedStyle(node).overflowY)) continue;
        const limit = node.scrollHeight - node.clientHeight;
        if (limit > 1 && (delta < 0 ? node.scrollTop > 0 : node.scrollTop < limit - 1)) return true;
      }
      return false;
    }

    bindScrollLock(signal) {
      document.addEventListener('touchstart', event => {
        this.scrollTouchY = this.isOpen() && event.touches.length === 1 ? event.touches[0].clientY : null;
      }, { signal, passive: true, capture: true });
      document.addEventListener('touchmove', event => {
        if (!this.isOpen() || event.touches.length !== 1) return;
        const y = event.touches[0].clientY;
        const delta = this.scrollTouchY == null ? 0 : this.scrollTouchY - y;
        this.scrollTouchY = y;
        if (!this.canScrollMenu(event.target, delta) && event.cancelable) event.preventDefault();
      }, { signal, passive: false, capture: true });
      const clearTouch = () => { this.scrollTouchY = null; };
      document.addEventListener('touchend', clearTouch, { signal, passive: true, capture: true });
      document.addEventListener('touchcancel', clearTouch, { signal, passive: true, capture: true });
      document.addEventListener('wheel', event => {
        if (this.isOpen() && !event.ctrlKey && event.deltaY && !this.canScrollMenu(event.target, event.deltaY) && event.cancelable) event.preventDefault();
      }, { signal, passive: false, capture: true });
    }

    lockPage() {
      if (this.scrollLock) return;
      const html = document.documentElement;
      this.scrollLock = {
        x: scrollX, y: scrollY,
        controller: new AbortController(),
        html: saveStyles(html, ['overflow', 'overflow-x', 'overflow-y', 'overscroll-behavior', 'scrollbar-gutter'])
      };
      this.bindScrollLock(this.scrollLock.controller.signal);
      // Keep the document and sticky header at their real scroll position.
      html.style.setProperty('scrollbar-gutter', 'stable', 'important');
      html.style.setProperty('overflow', 'hidden', 'important');
      html.style.setProperty('overscroll-behavior', 'none', 'important');
    }

    unlockPage() {
      const lock = this.scrollLock;
      if (!lock) return;
      this.scrollLock = null;
      lock.controller.abort();
      this.scrollTouchY = null;
      restoreStyles(document.documentElement, lock.html);
      if (scrollX === lock.x && scrollY === lock.y) return;
      const behavior = saveStyles(document.documentElement, ['scroll-behavior']);
      document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(lock.x, lock.y);
      restoreStyles(document.documentElement, behavior);
    }

    open() {
      if (!this.mobile.matches || this.buying || !this.isConnected) return;
      if (this.closePromise || this.classList.contains('is-closing')) this.finishClose(false);
      if (this.isOpen()) return;
      this.resetPanels();
      this.warmed.clear();
      this.lockPage();
      try { this.openSurface(); } catch (_) {
        this.closeSurface();
        this.dataset.rnfMmState = 'closed';
        this.unlockPage();
        return;
      }
      this.toggle.setAttribute('aria-expanded', 'true');
      this.viewport(true);
      this.classList.remove('is-closing');
      this.dialog.classList.remove('is-closing');
      this.dialog.focus({ preventScroll: true });
      const distance = this.sheet.offsetHeight + 12;
      this.classList.add('is-open');
      this.dialog.classList.add('is-open');
      this.dataset.rnfMmState = 'opening';
      this.animateSheet(`translate3d(0,${distance}px,0)`, 'translate3d(0,0,0)', () => {
        this.dataset.rnfMmState = 'open';
        this.viewport(true);
      });
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (this.closePromise) {
        const closing = this.closePromise;
        if (immediate) this.finishClose(restoreFocus);
        return closing;
      }
      if (!this.isOpen()) return Promise.resolve();
      const currentTransform = getComputedStyle(this.sheet).transform;
      this.cancelSheetAnimation();
      this.finishNavigation();
      this.resetDrag();
      this.classList.remove('is-open');
      this.dialog.classList.remove('is-open');
      this.classList.add('is-closing');
      this.dialog.classList.add('is-closing');
      this.dataset.rnfMmState = 'closing';
      if (immediate || this.reducedMotion()) { this.finishClose(restoreFocus); return Promise.resolve(); }
      this.closePromise = new Promise(resolve => { this.closeResolve = resolve; });
      const closing = this.closePromise;
      this.animateSheet(currentTransform, `translate3d(0,${this.sheet.offsetHeight + 12}px,0)`, () => this.finishClose(restoreFocus));
      return closing;
    }

    finishClose(restoreFocus) {
      if (!this.dialog) return;
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
      this.cancelSheetAnimation();
      this.finishNavigation();
      this.resetDrag();
      this.dataset.rnfMmState = 'closed';
      this.finishing = true;
      this.closeSurface();
      this.finishing = false;
      this.classList.remove('is-open', 'is-closing');
      this.dialog.classList.remove('is-open', 'is-closing');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.unlockPage();
      this.resetPanels();
      if (restoreFocus && this.isConnected) this.toggle.focus({ preventScroll: true });
      if (!this.keyboardFocus && document.activeElement === this.toggle) this.toggle.blur();
      const resolve = this.closeResolve;
      this.closeResolve = null;
      this.closePromise = null;
      resolve?.();
    }

    resetPanels() {
      this.finishNavigation();
      this.path = ['root'];
      this.panels.forEach((panel, key) => { panel.hidden = key !== 'root'; });
      this.updateNavigation();
    }

    updateNavigation() {
      const active = this.path[this.path.length - 1];
      this.dataset.panel = active;
      this.dataset.level = String(this.path.length - 1);
      this.dialog.dataset.level = this.dataset.level;
      if (this.backButton) this.backButton.hidden = active === 'root';
      if (this.backLabel) this.backLabel.textContent = this.labels.get(active) || '';
      this.dialog.querySelectorAll('[data-rnf-mm-to]').forEach(button => {
        button.setAttribute('aria-expanded', String(this.path.includes(button.dataset.rnfMmTo)));
        const panel = this.panels.get(button.dataset.rnfMmTo);
        if (panel?.id) button.setAttribute('aria-controls', panel.id);
      });
    }

    navigate(key, source, backwards = false) {
      if (!this.isOpen() || this.closePromise || !this.panels.has(key) || this.dataset.panel === key) return;
      this.finishNavigation();
      const previous = this.panels.get(this.dataset.panel), next = this.panels.get(key);
      if (source && !this.labels.get(key)) this.labels.set(key, source.textContent.trim());
      const existing = this.path.indexOf(key);
      if (backwards || existing !== -1) this.path = this.path.slice(0, existing + 1);
      else this.path.push(key);
      this.dataset.direction = backwards ? 'back' : 'forward';
      this.dialog.dataset.direction = this.dataset.direction;
      this.updateNavigation();
      next.hidden = false;
      next.scrollTop = 0;
      previous?.setAttribute('inert', '');
      previous?.classList.add('is-leaving');
      next.classList.add('is-entering');
      this.navigation = { previous, next };
      const focusTarget = next.querySelector('[data-rnf-mm-panel-title],h2,h3') || next;
      if (!focusTarget.hasAttribute('tabindex')) focusTarget.tabIndex = -1;
      focusTarget.focus({ preventScroll: true });
      if (this.reducedMotion()) this.finishNavigation();
      else this.navigationTimer = setTimeout(() => this.finishNavigation(), 500);
      this.warmPanel(next);
    }

    back() {
      if (this.path.length > 1) this.navigate(this.path[this.path.length - 2], null, true);
      else this.close();
    }

    finishNavigation() {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = null;
      if (!this.navigation) return;
      const { previous, next } = this.navigation;
      this.navigation = null;
      if (previous && previous !== next) previous.hidden = true;
      previous?.removeAttribute('inert');
      previous?.classList.remove('is-leaving');
      next.classList.remove('is-entering');
    }

    warm(button) {
      if (button?.dataset.rnfMmMode === 'direct') return;
      const url = productURL(button?.dataset.productUrl);
      if (!url || !this.isOpen() || button.disabled || this.warmed.has(url)) return;
      const api = window.RNFQuickBuy;
      if (!api?.prepare && !api?.prefetch) return;
      this.warmed.add(url);
      try { Promise.resolve(api.prepare ? api.prepare(url) : api.prefetch(url)).then(ready => { if (ready === false) this.warmed.delete(url); }).catch(() => this.warmed.delete(url)); }
      catch (_) { this.warmed.delete(url); }
    }

    warmIntent(event) {
      const button = event.target.closest?.('[data-rnf-mm-buy]');
      if (button && this.owns(button)) this.warm(button);
    }

    warmPanel(panel) {
      [...panel.querySelectorAll('[data-rnf-mm-buy]')].filter(button => !button.disabled && button.dataset.rnfMmMode !== 'direct').slice(0, 2).forEach(button => this.warm(button));
    }

    purchaseFocus(button) {
      return this.owns(button) ? this.mobile.matches ? this.toggle : document.getElementById('cart-icon-bubble') || this.toggle : button;
    }

    async cartEngine() {
      if (!window.RNFCart?.run && document.querySelector('script[src*="/rnf-native-cart.js"]')) {
        let timer;
        await Promise.race([customElements.whenDefined('cart-drawer'), new Promise(resolve => { timer = setTimeout(resolve, 2000); })]);
        clearTimeout(timer);
      }
      return window.RNFCart?.run ? window.RNFCart : null;
    }

    async showCart(opener, refresh = false) {
      const drawer = document.getElementById('RnfCartDrawer');
      if (drawer) {
        if (!refresh && window.RNFCart?.isReady?.() !== false && drawer.reveal) drawer.reveal(opener);
        else if (drawer.open) await drawer.open(opener);
        else if (drawer.reveal) drawer.reveal(opener);
        return;
      }
      const url = new URL(this.dataset.cartUrl || '/cart', location.href);
      if (url.origin === location.origin && /\/cart\/?$/.test(url.pathname)) location.assign(url.pathname + url.search);
    }

    async addDirect(button, id, external) {
      const engine = await this.cartEngine();
      if (!engine) return false;
      const label = button.getAttribute('aria-label');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('data-rnf-mm-adding', '');
      if (button.dataset.addingLabel) button.setAttribute('aria-label', button.dataset.addingLabel);
      try {
        await engine.run(api => api.add({ items: [{ id, quantity: 1 }] }), { open: false, opener: this.purchaseFocus(button) });
        window.RNFStrapOffer?.prepare?.(id);
        if (!external) await this.close({ restoreFocus: false });
        if (!this.isConnected) return true;
        await window.RNFQuickBuy?.confirmAdded?.(button);
        if (!this.isConnected) return true;
        const opener = this.purchaseFocus(button);
        if (engine.isReady?.() !== false && window.RNFStrapOffer?.afterAdd?.(id, opener)) return true;
        await this.showCart(opener);
      } catch (_) {
        if (!external) await this.close({ restoreFocus: false });
        if (this.isConnected) await this.showCart(this.purchaseFocus(button), true);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-rnf-mm-adding');
        if (label === null) button.removeAttribute('aria-label'); else button.setAttribute('aria-label', label);
      }
      return true;
    }

    async buy(button, event, external = false) {
      if (this.buying || button.disabled || (!external && !this.isOpen())) return;
      const url = productURL(button.dataset.productUrl);
      if (!url) return;
      this.buying = true;
      try {
        const id = button.dataset.variantId;
        if (button.dataset.rnfMmMode === 'direct' && /^\d+$/.test(id || '')) {
          if (await this.addDirect(button, id, external)) return;
        }
        await this.openQuickBuy(button, event, url, external);
      } finally { this.buying = false; }
    }

    async openQuickBuy(button, event, url, external) {
      const detail = event.detail;
      this.warm(button);
      if (!external) await this.close({ restoreFocus: false });
      if (!this.isConnected) return;
      if (!window.RNFQuickBuy || !document.getElementById('RnfQuickBuy')) {
        location.assign(url);
        return;
      }
      this.proxyFocus = button;
      if (!this.proxy) {
        const proxy = document.createElement('button');
        proxy.type = 'button';
        proxy.className = 'rnf-qb__trigger';
        proxy.hidden = true;
        proxy.tabIndex = -1;
        proxy.setAttribute('aria-hidden', 'true');
        proxy.style.setProperty('display', 'none', 'important');
        proxy.focus = options => {
          if (this.isConnected && !document.querySelector('dialog[open]')) this.purchaseFocus(this.proxyFocus).focus(options || { preventScroll: true });
        };
        this.append(proxy);
        this.proxy = proxy;
      }
      this.proxy.dataset.rnfQuickBuy = url;
      this.proxy.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, detail }));
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      this.keyboardFocus = event.detail === 0;
      // A modal dialog owns pointer hits over the transparent header area.
      const rect = target === this.dialog && event.detail > 0 ? this.toggle.getBoundingClientRect() : null;
      const overlayToggle = rect && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (target.closest('[data-rnf-mm-toggle]') === this.toggle || overlayToggle) {
        event.preventDefault();
        if (this.closePromise || this.classList.contains('is-closing')) this.open();
        else if (this.isOpen()) this.close();
        else this.open();
        return;
      }
      if (!this.isOpen() || !this.dialog.contains(target)) return;
      const buy = target.closest('[data-rnf-mm-buy]');
      if (buy) { event.preventDefault(); this.buy(buy, event); return; }
      const destination = target.closest('[data-rnf-mm-to]');
      if (destination) { event.preventDefault(); this.navigate(destination.dataset.rnfMmTo, destination); return; }
      if (target.closest('[data-rnf-mm-back]')) { event.preventDefault(); this.back(); return; }
      if (target.closest('[data-rnf-mm-close]')) {
        event.preventDefault();
        if (performance.now() >= this.suppressClickUntil) this.close();
        return;
      }
      if (target === this.dialog) { this.close(); return; }
      const link = target.closest('a[href]');
      if (link?.hasAttribute('data-rnf-mm-reviews')) {
        const url = new URL(link.href, location.href);
        if (this.findReviews() && url.origin === location.origin && url.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '')) {
          this.visitReviews(link, event);
          return;
        }
      }
      if (link && !link.closest('localization-form')) this.close({ restoreFocus: false, immediate: true });
    }

    bindDrag(signal) {
      if (!this.handle) return;
      const start = (y, id) => {
        if (!this.isOpen() || this.closePromise || this.sheetAnimation) return;
        this.keyboardFocus = false;
        this.drag = { start: y, distance: 0, id };
        this.sheet.classList.add('is-dragging');
      };
      const move = (y, event) => {
        if (!this.drag) return;
        this.drag.distance = Math.max(0, y - this.drag.start);
        this.sheet.style.setProperty('transform', `translate3d(0, ${this.drag.distance}px, 0)`);
        if (this.drag.distance > 4 && event.cancelable) event.preventDefault();
      };
      const end = cancelled => {
        if (!this.drag) return;
        const distance = this.drag.distance;
        if (distance > 4) this.suppressClickUntil = performance.now() + 400;
        if (!cancelled && distance >= 70) this.close();
        else {
          const current = getComputedStyle(this.sheet).transform;
          this.resetDrag();
          if (distance > 0) this.animateSheet(current, 'translate3d(0,0,0)', () => this.viewport(true));
        }
      };
      if ('PointerEvent' in window) {
        this.handle.addEventListener('pointerdown', event => {
          if (event.button !== 0 || event.isPrimary === false) return;
          start(event.clientY, event.pointerId);
          try { if (this.drag) this.handle.setPointerCapture(event.pointerId); } catch (_) {}
        }, { signal });
        this.handle.addEventListener('pointermove', event => {
          if (this.drag?.id === event.pointerId) move(event.clientY, event);
        }, { signal, passive: false });
        this.handle.addEventListener('pointerup', event => { if (this.drag?.id === event.pointerId) end(false); }, { signal });
        this.handle.addEventListener('pointercancel', event => { if (this.drag?.id === event.pointerId) end(true); }, { signal });
      } else {
        this.handle.addEventListener('touchstart', event => { if (event.touches.length === 1) start(event.touches[0].clientY, null); }, { signal, passive: true });
        this.handle.addEventListener('touchmove', event => {
          if (event.touches.length === 1) move(event.touches[0].clientY, event);
          else end(true);
        }, { signal, passive: false });
        this.handle.addEventListener('touchend', () => end(false), { signal });
        this.handle.addEventListener('touchcancel', () => end(true), { signal });
      }
    }

    resetDrag() {
      if (this.drag?.id != null) {
        try { this.handle.releasePointerCapture(this.drag.id); } catch (_) {}
      }
      this.drag = null;
      this.sheet?.classList.remove('is-dragging');
      this.sheet?.style.removeProperty('transform');
    }
  }

  customElements.define('rnf-mobile-menu', RnfMobileMenu);
})();









